import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  Hammer,
  BrickWall,
  Flame,
  Zap,
  Timer,
  Clock,
  Award,
  Activity,
  Trophy,
  Footprints,
  Rocket,
  Lock,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import CardHead from './CardHead';
import { TennisBall } from '@/assets/icons/TennisBall';
import type { SegmentStatistics } from '@/types/statistics';
import {
  transformMatchSummary,
  transformPlayerAwards,
  type PlayerAward,
} from '@/lib/transforms';
import { formatDistance, formatSpeedKmh } from '@/lib/utils';
import { SHOW_WINNERS } from '@/lib/constants';
import { usePlayerNameResolver } from '@/contexts/PlayerNamesContext';
import type { PlayerColor } from './playerColors';
import { StyleDiverging, type StyleDatum } from './StyleOfPlay';
import ShotRadar, { type RadarDatum } from './ShotRadar';
import ShotSpeedBars from './ShotSpeedBars';
import LobAnalysis from './LobAnalysis';
import {
  buildShotSpeedRows,
  buildLobGroupMetrics,
  type LobSelection,
  type ShotSpeedRow,
} from './shotAnalytics';
import { playerShotRadarValues, playerStyleRatios } from './tournamentMetrics';
import PositionCard from './PositionCard';
import ContributionCard from './ContributionCard';
import ImprovementTips from './ImprovementTips';
import LockedPreview, { UnlockCta } from './LockedPreview';
import {
  SAMPLE_LOBS,
  SAMPLE_RACE_SEGMENT,
  sampleRadar,
  sampleSegment,
  sampleSpeedRows,
  sampleStyle,
} from './upsellSample';

// The Visão Geral panel: a one-glance read led by the match as a whole — the
// rally-time ring + standouts podium — then scoped to the player viewing the
// report: their identity + headline vitals, position, contribution and shots.
// The court itself lives in TournamentReport so it survives the mega-tab switch
// without re-drawing.
interface OverviewPageProps {
  segment: SegmentStatistics;
  crops: Record<string, string>;
  colors: Record<string, PlayerColor>;
  /** The player the summary is scoped to (defaults to player1 upstream). */
  focusPlayerId: string;
  /** Gates shot-dependent tiles (total shots, shots/rally). */
  isAdvanced: boolean;
}

const Eyebrow: React.FC<{ children: React.ReactNode; beta?: boolean }> = ({
  children,
  beta,
}) => (
  <div className="eyebrow">
    <span className="eyebrow-pt">{children}</span>
    {beta ? <span className="eyebrow-beta">beta</span> : null}
  </div>
);

/**
 * Style of play, shot-type mix and per-stroke speed in one card. Extracted so
 * the real card and its locked sample preview render through one definition —
 * the preview's whole job is to show the shape of the real thing.
 */
const ShotsCard: React.FC<{
  style: StyleDatum[];
  radar: RadarDatum[];
  speed: ShotSpeedRow[] | null;
  /** Only the real card is a tour target; the sample isn't worth spotlighting. */
  tour?: string;
}> = ({ style, radar, speed, tour }) => {
  const { t } = useTranslation('tournament');
  const tl = (k: string) => t(`label.${k}` as never) as string;
  return (
    <div className="card shots-card" data-tour={tour}>
      <CardHead
        icon={TennisBall}
        title={t('overview.shotsTitle')}
        subtitle={t('overview.shotsSub')}
      />
      {/* Widest read first: the attack/defence split in one bar, then the
          eight-axis mix behind it, then how hard it was hit. */}
      {style.length ? (
        <div className="shots-section">
          <Eyebrow>{tl('styleOfPlay')}</Eyebrow>
          <StyleDiverging data={style} />
        </div>
      ) : null}
      {radar.length ? (
        <div className="shots-section">
          <Eyebrow>{tl('shotTypes')}</Eyebrow>
          <ShotRadar data={radar} />
        </div>
      ) : null}
      {speed ? (
        <div className="shots-section">
          {/* Speed is derived from the fitted 3-D trajectory, the newest and
              least settled thing in the payload — flagged so a reader weighs
              it accordingly. */}
          <Eyebrow beta>{tl('shotSpeed')}</Eyebrow>
          <ShotSpeedBars rows={speed} />
        </div>
      ) : null}
    </div>
  );
};

// Staggered fade-up so the panel assembles top-to-bottom on entry. Index drives
// the delay; the ring inside the Partida card still draws itself separately.
const Reveal: React.FC<{ i: number; children: React.ReactNode }> = ({
  i,
  children,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{
      duration: 0.42,
      delay: i * 0.06,
      ease: [0.22, 0.61, 0.36, 1],
    }}
  >
    {children}
  </motion.div>
);

// FIP-styled circular rally-time ring: the share of match time the ball was in
// play. Draws in once on mount.
const RallyRing: React.FC<{
  rallyPct: number; // 0-100
  rallyMin: number;
  totalMin: number;
}> = ({ rallyPct, rallyMin, totalMin }) => {
  const { t } = useTranslation('tournament');
  const size = 200;
  const stroke = 13;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const [draw, setDraw] = useState(0);

  useEffect(() => {
    const id = setTimeout(() => setDraw(rallyPct), 220);
    return () => clearTimeout(id);
  }, [rallyPct]);

  return (
    <div className="rally-ring">
      <div className="rally-ring-svg">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke="var(--hairline)"
              strokeWidth={stroke}
            />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke="var(--accent)"
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={`${(draw / 100) * circ} ${circ}`}
              style={{
                transition:
                  'stroke-dasharray 1100ms cubic-bezier(0.5,0.1,0.2,1)',
              }}
            />
          </g>
        </svg>
        {/* Center: the absolute split (rally vs total minutes). */}
        <div className="rally-ring-center">
          <span className="rally-ring-badge">
            <Timer size={18} strokeWidth={2} />
          </span>
          <div className="rally-ring-block">
            <span className="rally-ring-k">{t('ring.rallyTime')}</span>
            <span className="rally-ring-v accent">
              {rallyMin}
              <span className="rally-ring-u">{t('ring.min')}</span>
            </span>
          </div>
          <span className="rally-ring-split" />
          <div className="rally-ring-block">
            <span className="rally-ring-k">{t('ring.totalTime')}</span>
            <span className="rally-ring-v">
              {totalMin}
              <span className="rally-ring-u">{t('ring.min')}</span>
            </span>
          </div>
        </div>
      </div>
      {/* Below: the proportion in play. */}
      <div className="rally-ring-pctblock">
        <span className="rally-ring-pct">{Math.round(rallyPct)}%</span>
        <span className="rally-ring-caption">{t('ring.caption')}</span>
      </div>
    </div>
  );
};

// One stat tile inside the merged Partida card (light, bordered).
const Tile: React.FC<{
  icon: typeof Clock | typeof TennisBall;
  value: React.ReactNode;
  unit?: string;
  label: string;
}> = ({ icon: Icon, value, unit, label }) => (
  <div className="partida-tile">
    <span className="partida-tile-badge">
      <Icon size={15} strokeWidth={2} />
    </span>
    <div className="partida-tile-val">
      <span className="partida-tile-num">{value}</span>
      {unit ? <span className="partida-tile-unit">{unit}</span> : null}
    </div>
    <div className="partida-tile-label">{label}</div>
  </div>
);

/** Any lucide glyph — every award medal is one. */
type AwardIcon = typeof Hammer;

// Each prize has a NAME (the thing worth winning) and a description (what it
// measures). `awards.name.*` carries the name; `awards.<category>` stays the
// plain-language description underneath it.
const AWARD_META: Record<
  PlayerAward['category'],
  { Icon: AwardIcon; nameKey: string; labelKey: string; statKey: string }
> = {
  mostOffensive: {
    Icon: Hammer,
    nameKey: 'awards.name.mostOffensive',
    labelKey: 'awards.mostOffensive',
    statKey: 'awards.offensiveShots',
  },
  mostConsistent: {
    Icon: BrickWall,
    nameKey: 'awards.name.mostConsistent',
    labelKey: 'awards.mostConsistent',
    statKey: 'awards.errorRate',
  },
  mostDecisive: {
    Icon: Flame,
    nameKey: 'awards.name.mostDecisive',
    labelKey: 'awards.mostDecisive',
    statKey: 'awards.winnerRate',
  },
  hardestHitter: {
    Icon: Rocket,
    nameKey: 'awards.name.hardestHitter',
    labelKey: 'awards.hardestHitter',
    statKey: 'awards.ballSpeed',
  },
};

interface Highlight {
  key: string;
  /** The prize's name — "O Relâmpago". The headline of the card. */
  title: string;
  /** The number itself, at hero size. */
  value: string;
  /** Unit, set beside the number at a smaller size ("m", "km", "km/h"). */
  unit?: string;
  /** What the number measures, in words, under it. */
  caption?: string;
  Icon: AwardIcon;
  playerId: string;
  crop?: string;
  label: string;
  color: string;
  /** A prize this report can't award (basic mode): the name and what it
      measures stay legible, the winner and the number are mocked and fogged. */
  locked?: boolean;
}

// `custom` feeds the direction in, so a card leaves toward whichever side the
// reader asked for rather than always the same way.
const AWARD_SLIDE = {
  enter: (d: number) => ({ opacity: 0, x: d >= 0 ? 34 : -34 }),
  center: { opacity: 1, x: 0 },
  exit: (d: number) => ({ opacity: 0, x: d >= 0 ? -34 : 34 }),
};

/** Gold sparks thrown outward from the portrait — only when the prize is the
    reader's. Twelve fixed angles, so it reads as a burst rather than noise. */
const SPARKS = 16;

const AwardBurst: React.FC = () => (
  <span className="award-burst" aria-hidden="true">
    {/* A ring that expands past the portrait and fades — the shockwave the
        sparks fly out of. Does most of the work at a glance; the sparks are
        the detail you notice second. */}
    <motion.span
      className="award-shock"
      initial={{ scale: 0.35, opacity: 0.85 }}
      animate={{ scale: 1.9, opacity: 0 }}
      transition={{ duration: 0.72, ease: [0.16, 0.8, 0.3, 1] }}
    />
    {Array.from({ length: SPARKS }).map((_, i) => {
      // Alternate the throw distance so the ring of sparks doesn't read as a
      // single mechanical circle.
      const a = (i / SPARKS) * Math.PI * 2;
      const reach = i % 2 ? 78 : 104;
      return (
        <motion.span
          key={i}
          className="award-spark"
          initial={{ x: 0, y: 0, opacity: 0, scale: 0.3 }}
          animate={{
            x: Math.cos(a) * reach,
            y: Math.sin(a) * reach,
            opacity: [0, 1, 1, 0],
            scale: [0.3, 1.15, 0.9, 0.4],
          }}
          transition={{
            duration: 1,
            delay: 0.05 + (i % 4) * 0.035,
            ease: [0.18, 0.7, 0.3, 1],
          }}
        />
      );
    })}
  </span>
);

/**
 * The standouts, one at a time.
 *
 * A grid of small tiles asks the reader to compare; a prize is not a
 * comparison, it's a reveal. So each award gets the whole card: the winner's
 * face at size, lit from behind in their own identity colour, the prize named
 * above it and the number that won it carried at hero size below. Stepping to
 * the next one is a deliberate beat — arrows and dots, with the card sliding
 * in the direction you asked for.
 *
 * When the prize turns out to be the reader's, the card says so and throws
 * sparks: that is the whole reason this section exists.
 */
const Awards: React.FC<{ highlights: Highlight[]; focusPlayerId: string }> = ({
  highlights,
  focusPlayerId,
}) => {
  const { t } = useTranslation('tournament');
  const resolveName = usePlayerNameResolver();
  // [index, direction] together, so the exit animation knows which way the
  // card should leave rather than inferring it from a stale previous index.
  const [[idx, dir], setPos] = useState<[number, number]>([0, 0]);
  const reduceMotion = useReducedMotion();

  const total = highlights.length;
  const h = total ? highlights[Math.min(idx, total - 1)] : undefined;
  // A flying, flashing burst is exactly what a reduced-motion preference is
  // asking not to see; the gold name and card edge still mark the prize.
  const celebrate = !!h && h.playerId === focusPlayerId && !reduceMotion;
  const isMe = !!h && h.playerId === focusPlayerId;

  if (!h) return null;

  const Icon = h.Icon;
  const name = resolveName(h.playerId);
  const go = (step: number) =>
    setPos(([i]) => [(i + step + total) % total, step]);

  return (
    <div className="card awards-card" data-tour="awards">
      <CardHead
        icon={Trophy}
        title={t('awards.title')}
        subtitle={t('awards.subtitle')}
      />

      <div className="award-stage">
        <AnimatePresence initial={false} mode="wait" custom={dir}>
          <motion.div
            key={h.key}
            className={`award-card${isMe ? ' is-me' : ''}${
              h.locked ? ' is-locked' : ''
            }`}
            custom={dir}
            variants={AWARD_SLIDE}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.28, ease: [0.22, 0.61, 0.36, 1] }}
          >
            {/* The winner's identity colour, thrown behind the portrait. */}
            <span
              className="award-glow"
              aria-hidden="true"
              style={{ background: h.color }}
            />

            <span className="award-cat">{h.title}</span>
            <span className="award-desc">{h.label}</span>

            <span className="award-portrait">
              {/* No trigger state: the whole card remounts on every step
                  (AnimatePresence is keyed on h.key), so the burst plays
                  itself in whenever you arrive at one of your own prizes. */}
              {celebrate && <AwardBurst />}
              <motion.span
                className="award-ring"
                style={{ borderColor: h.color }}
                initial={celebrate ? { scale: 0.74 } : false}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 430, damping: 13 }}
              >
                {h.crop ? <img src={h.crop} alt={name} /> : null}
              </motion.span>
              {/* The prize itself, pinned to the portrait like a medal. */}
              <span className="award-medal">
                <Icon size={16} strokeWidth={2.2} />
              </span>
            </span>

            {/* The viewer's own name already resolves to "Tu", so a separate
                "És tu" badge under it would say the same thing twice. One
                line, gold, doing both jobs. */}
            {/* A locked prize has no winner, so the name is a placeholder the
                CSS fogs along with the portrait — leaving it legible would
                credit a real player with a trophy nobody was awarded. */}
            <span className={`award-name${isMe ? ' is-you' : ''}`}>
              {h.locked ? '—' : isMe ? (t('awards.itsYou') as string) : name}
            </span>

            <span className="award-value">
              {h.value}
              {h.unit && <span className="award-unit">{h.unit}</span>}
            </span>
            {h.caption && <span className="award-caption">{h.caption}</span>}
            {/* The prize's own call to action (same as the card previews). */}
            {h.locked && (
              <UnlockCta className="award-cta">
                <Lock size={11} strokeWidth={2.6} aria-hidden="true" />
                {t('locked.cta')}
              </UnlockCta>
            )}
            {h.locked && (
              <span className="award-note">{t('locked.illustrative')}</span>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="award-nav">
        <button
          type="button"
          className="award-arrow"
          onClick={() => go(-1)}
          aria-label={t('awards.prev')}
        >
          <ChevronLeft size={17} strokeWidth={2.2} />
        </button>
        <div className="award-dots">
          {highlights.map((x, i) => (
            <button
              key={x.key}
              type="button"
              className={`award-dot${i === idx ? ' active' : ''}`}
              onClick={() => setPos([i, i >= idx ? 1 : -1])}
              aria-label={x.title}
              aria-current={i === idx ? 'true' : undefined}
            />
          ))}
        </div>
        <button
          type="button"
          className="award-arrow"
          onClick={() => go(1)}
          aria-label={t('awards.next')}
        >
          <ChevronRight size={17} strokeWidth={2.2} />
        </button>
      </div>
    </div>
  );
};

const OverviewPage: React.FC<OverviewPageProps> = ({
  segment,
  crops,
  colors,
  focusPlayerId,
  isAdvanced,
}) => {
  const { t } = useTranslation('tournament');
  const tl = (k: string) => t(`label.${k}` as never) as string;
  const tLocked = (k: string) => t(`locked.hook.${k}` as never) as string;
  const resolveName = usePlayerNameResolver();
  const stats = segment.statistics;

  // Basic reports carry no shot tracking, so the three shot-derived cards
  // can't render at all. Rather than leave the viewer guessing at what the
  // advanced report adds, draw those cards from sample data behind fog as a
  // preview. Built once per segment — `null` on advanced, where the real
  // cards render and nothing is locked.
  const locked = useMemo(
    () => (isAdvanced ? null : sampleSegment(segment)),
    [isAdvanced, segment],
  );
  const ms = useMemo(
    () => transformMatchSummary(stats.match_summary),
    [stats.match_summary],
  );

  const awards = useMemo(
    () =>
      transformPlayerAwards(
        stats.shots_breakdown,
        stats.shots_tracking_performance,
        crops,
        '',
        [],
        stats.players_statistics,
      ),
    [
      stats.shots_breakdown,
      stats.shots_tracking_performance,
      stats.players_statistics,
      crops,
    ],
  );

  // Standout highlights: the shot-based awards plus the own metrics computed
  // straight from players_statistics — who covered the most ground and who
  // moved fastest (the latter always present, so basic reports still get a pair).
  const highlights = useMemo<Highlight[]>(() => {
    const list: Highlight[] = (awards ?? []).map((a) => {
      const meta = AWARD_META[a.category];
      return {
        key: a.category,
        Icon: meta.Icon,
        title: t(meta.nameKey as never) as string,
        playerId: a.playerId,
        crop: a.playerCrop || undefined,
        label: t(meta.labelKey as never) as string,
        value: a.statValue,
        unit: a.statUnit,
        caption: t(meta.statKey as never) as string,
        color: colors[a.playerId]?.solid ?? 'var(--accent)',
      };
    });

    let topDistId = '';
    let topDist = -1;
    let topSpeedId = '';
    let topSpeed = -1;
    for (const [pid, p] of Object.entries(stats.players_statistics)) {
      if (p.running_distance > topDist) {
        topDist = p.running_distance;
        topDistId = pid;
      }
      if (p.mean_velocity > topSpeed) {
        topSpeed = p.mean_velocity;
        topSpeedId = pid;
      }
    }
    if (topDistId) {
      const d = formatDistance(topDist);
      list.push({
        key: 'mostDistance',
        Icon: Footprints,
        title: t('awards.name.mostDistance' as never) as string,
        playerId: topDistId,
        crop: crops[topDistId] || undefined,
        label: t('awards.mostDistance' as never) as string,
        value: d.value,
        unit: d.unit,
        caption: t('awards.distanceCovered' as never) as string,
        color: colors[topDistId]?.solid ?? 'var(--accent)',
      });
    }
    if (topSpeedId) {
      const s = formatSpeedKmh(topSpeed);
      list.push({
        key: 'fastest',
        Icon: Zap,
        title: t('awards.name.fastest' as never) as string,
        playerId: topSpeedId,
        crop: crops[topSpeedId] || undefined,
        label: t('awards.fastest' as never) as string,
        value: s.value,
        unit: s.unit,
        caption: t('awards.avgSpeed' as never) as string,
        color: colors[topSpeedId]?.solid ?? 'var(--accent)',
      });
    }

    // The prizes a basic report can't award at all — shot-derived, so nothing
    // in the payload can name a winner. They're added as mocked cards rather
    // than left out: the prize name and what it measures are the pitch, and a
    // podium missing half its trophies doesn't tell the viewer they exist.
    if (!isAdvanced) {
      for (const [category, meta] of Object.entries(AWARD_META)) {
        if (category === 'mostDecisive' && !SHOW_WINNERS) continue;
        list.push({
          key: `locked-${category}`,
          Icon: meta.Icon,
          title: t(meta.nameKey as never) as string,
          // No winner to name, so nothing real is borrowed here — the portrait
          // and the name are fogged, and the colour is the neutral accent.
          playerId: '',
          label: t(meta.labelKey as never) as string,
          value: category === 'hardestHitter' ? '50.0' : '50.0%',
          unit: category === 'hardestHitter' ? 'km/h' : undefined,
          caption: t(meta.statKey as never) as string,
          color: 'var(--accent)',
          locked: true,
        });
      }
    }
    return list;
  }, [awards, stats.players_statistics, crops, colors, isAdvanced, t]);

  // ---- the focus player ------------------------------------------------
  // Forehand / backhand / both — the lob card's own subtype filter, kept
  // separate from anything else on the panel.
  const [lobSelection, setLobSelection] = useState<LobSelection>('both');
  const ps = stats.players_statistics[focusPlayerId];
  const name = resolveName(focusPlayerId);
  const color = colors[focusPlayerId]?.solid ?? 'var(--accent)';
  const crop = crops[focusPlayerId];

  // How they play — offensive vs defensive.
  const style = playerStyleRatios(stats.shots_breakdown, focusPlayerId);
  const styleData: StyleDatum[] = style
    ? [{ id: focusPlayerId, name, color, crop, ...style }]
    : [];

  // Lobs — the same card the In Depth tab shows for a single player, scoped
  // here to the viewer. One group of one, so it always renders the spatial
  // court rather than the comparison pills.
  const lobGroups = useMemo(
    () => [{ id: focusPlayerId, playerIds: [focusPlayerId] }],
    [focusPlayerId],
  );
  // 'both' doubles as the existence test: it falls back to the subtype
  // records when a player carries no combined one.
  const lobBoth = useMemo(
    () => buildLobGroupMetrics(lobGroups, stats.players_statistics, 'both'),
    [lobGroups, stats.players_statistics],
  );
  const lobSelected = useMemo(
    () =>
      lobSelection === 'both'
        ? lobBoth
        : buildLobGroupMetrics(
            lobGroups,
            stats.players_statistics,
            lobSelection,
          ),
    [lobBoth, lobGroups, lobSelection, stats.players_statistics],
  );
  const lobVisible = lobBoth.some((group) => group.metrics?.hasData);

  // What they hit — shot-type mix pentagon (axes built inside ShotRadar).
  const shotVals = playerShotRadarValues(stats.shots_breakdown, focusPlayerId);
  const shotRadarData: RadarDatum[] = shotVals
    ? [{ id: focusPlayerId, name, color, crop, values: shotVals }]
    : [];

  // How fast they hit it — the focus player alone (one id, never pooled here).
  const speedRows = useMemo(
    () => buildShotSpeedRows([focusPlayerId], stats.players_statistics),
    [focusPlayerId, stats.players_statistics],
  );

  let i = 0;

  return (
    <main className="content">
      {/* The match as a whole, first — the rally-time ring + stat tiles. */}
      <Reveal i={i++}>
        <div className="card partida-card">
          <CardHead
            icon={Activity}
            title={tl('match')}
            subtitle={t('overview.partidaSub', { count: ms.totalRallies })}
          />

          <RallyRing
            rallyPct={ms.rallyTimePercentage}
            rallyMin={ms.timeInPlay}
            totalMin={ms.totalGameTime}
          />

          <div className="partida-stats">
            <Tile
              icon={Clock}
              value={ms.averageRally}
              unit="s"
              label={tl('avgRallyDur')}
            />
            <Tile
              icon={Award}
              value={ms.longestRally}
              unit="s"
              label={tl('longest')}
            />
            {isAdvanced ? (
              <>
                <Tile
                  icon={TennisBall}
                  value={ms.totalShots}
                  label={tl('totalShots')}
                />
                <Tile
                  icon={Activity}
                  value={ms.averageShotsPerRally.toFixed(1)}
                  label={tl('avgRally')}
                />
              </>
            ) : null}
          </div>
        </div>
      </Reveal>

      {/* Standouts podium — who stood out across the match. */}
      {highlights.length ? (
        <Reveal i={i++}>
          <Awards highlights={highlights} focusPlayerId={focusPlayerId} />
        </Reveal>
      ) : null}

      {/* Position — movement vitals, depth occupancy, team formation. Gated on
          ps so we never emit an empty Reveal (the card returns null without it). */}
      {ps ? (
        <Reveal i={i++}>
          <div data-tour="position">
            <PositionCard
              segment={segment}
              crops={crops}
              colors={colors}
              focusPlayerId={focusPlayerId}
            />
          </div>
        </Reveal>
      ) : null}

      {/* Contribution — consistency, how points were lost, shot distribution.
          Shot-dependent: only render when the focus player has shot tracking,
          so basic-mode reports don't leave an empty Reveal gap. */}
      {ps?.shots_tracking ? (
        <Reveal i={i++}>
          <div data-tour="contribution">
            <ContributionCard
              segment={segment}
              crops={crops}
              colors={colors}
              focusPlayerId={focusPlayerId}
            />
          </div>
        </Reveal>
      ) : locked ? (
        <Reveal i={i++}>
          <LockedPreview hook={tLocked('contribution')}>
            <ContributionCard
              segment={locked}
              crops={crops}
              colors={colors}
              focusPlayerId={focusPlayerId}
              shotSegment={SAMPLE_RACE_SEGMENT}
            />
          </LockedPreview>
        </Reveal>
      ) : null}

      {/* Shots — style of play + shot-type mix, merged into one card. Speed
          alone is enough to justify the card when the mix/style are absent. */}
      {styleData.length || shotRadarData.length || speedRows ? (
        <Reveal i={i++}>
          <ShotsCard
            style={styleData}
            radar={shotRadarData}
            speed={speedRows}
            tour="shots"
          />
        </Reveal>
      ) : locked ? (
        <Reveal i={i++}>
          <LockedPreview hook={tLocked('shots')}>
            <ShotsCard
              style={sampleStyle(name, color)}
              radar={sampleRadar(name, color)}
              speed={sampleSpeedRows()}
            />
          </LockedPreview>
        </Reveal>
      ) : null}

      {/* Lobs — the same card the In Depth tab shows for one player, scoped
          here to the viewer. Brings its own card + eyebrow, and hides itself
          when the player has no usable lob records. */}
      {lobVisible ? (
        <Reveal i={i++}>
          <div data-tour="lobs">
            <LobAnalysis
              metrics={lobSelected[0]?.metrics ?? null}
              selection={lobSelection}
              onSelect={setLobSelection}
            />
          </div>
        </Reveal>
      ) : locked ? (
        <Reveal i={i++}>
          <LockedPreview hook={tLocked('lobs')}>
            <LobAnalysis
              metrics={SAMPLE_LOBS}
              selection={lobSelection}
              onSelect={setLobSelection}
            />
          </LockedPreview>
        </Reveal>
      ) : null}

      {/* AI coaching tips (beta) — the focus player's strengths and things to
          improve, straight from the model. Closes the panel as the takeaways.
          Data-driven: basic reports carry none, so this simply doesn't render. */}
      {ps?.coaching_tips?.length ? (
        <Reveal i={i++}>
          <div data-tour="tips">
            <ImprovementTips tips={ps.coaching_tips} />
          </div>
        </Reveal>
      ) : null}
    </main>
  );
};

export default OverviewPage;
